import { z } from 'zod';
import { isoDateTimeSchema, localTimeSchema, uuidSchema } from './common.js';
import { ianaTimezoneSchema } from './venue.js';
/**
 * Shared wire schemas for the notification surface (admin-arch §3.11 +
 * PRD §11.2-11.4, Wave 2.12 BE-1).
 *
 * Three resources are exposed:
 *   - templates (versioned email/push bodies + subjects)
 *   - triggers (per-event schedule + addressee config)
 *   - history (sent-notification audit log)
 *
 * Channels: only `email` is wired end-to-end in MVP; `push` / `in_app`
 * are admitted by the schema so seeding triggers for them does not require
 * a follow-up migration.
 */
export const notificationChannelEnum = z.enum(['email', 'push', 'in_app']);
export const notificationLanguageEnum = z.enum(['ru', 'en']);
export const notificationStatusEnum = z.enum([
    'queued',
    'sent',
    'delivered',
    'bounced',
    'failed',
]);
// Input caps — defence against pathological payloads (reviews I-1/I-2).
const CODE_MAX = 64;
const NAME_MAX = 255;
const SUBJECT_MAX = 998; // RFC 5322 subject hard cap
const BODY_MAX = 100_000; // generous for markdown; rejects "paste war and peace"
const VAR_NAME_MAX = 64;
const VAR_LIST_MAX = 64;
const ROLE_MAX = 32;
const EMAIL_MAX = 320;
const codeSlugSchema = z
    .string()
    .trim()
    .min(1)
    .max(CODE_MAX)
    .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case');
const variableNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(VAR_NAME_MAX)
    .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case');
// -------------------------------------------------------------------------
// Template
// -------------------------------------------------------------------------
export const notificationTemplateSchema = z.object({
    id: uuidSchema,
    code: codeSlugSchema,
    channel: notificationChannelEnum,
    language: notificationLanguageEnum,
    subject: z.string().max(SUBJECT_MAX).nullable(),
    body: z.string().max(BODY_MAX),
    variables: z.array(variableNameSchema).max(VAR_LIST_MAX).nullable(),
    version: z.number().int().positive(),
    is_active: z.boolean(),
    created_at: isoDateTimeSchema,
});
export const notificationTemplateListResponseSchema = z.object({
    items: z.array(notificationTemplateSchema),
});
export const notificationTemplateVersionsResponseSchema = z.object({
    items: z.array(notificationTemplateSchema),
});
/**
 * `PATCH /admin/notifications/templates/:code` — supplying any field creates
 * a NEW version row; `version` is assigned by the backend (current latest +
 * 1). Channel/language are part of the natural key — to target a specific
 * channel/language pair both must be sent.
 */
export const notificationTemplatePatchSchema = z
    .object({
    channel: notificationChannelEnum,
    language: notificationLanguageEnum.default('ru'),
    subject: z.string().max(SUBJECT_MAX).nullable().optional(),
    body: z.string().min(1).max(BODY_MAX),
    variables: z.array(variableNameSchema).max(VAR_LIST_MAX).optional(),
    is_active: z.boolean().optional(),
})
    .strict();
// -------------------------------------------------------------------------
// Trigger
// -------------------------------------------------------------------------
export const notificationTriggerSchema = z.object({
    id: uuidSchema,
    code: codeSlugSchema,
    name: z.string().min(1).max(NAME_MAX),
    channel: notificationChannelEnum,
    template_id: uuidSchema.nullable(),
    lead_time_days: z.array(z.number().int().nonnegative().max(365)).nullable(),
    is_enabled: z.boolean(),
    quiet_hours_start: localTimeSchema.nullable(),
    quiet_hours_end: localTimeSchema.nullable(),
    recipient_role: z.string().min(1).max(ROLE_MAX).nullable(),
    updated_at: isoDateTimeSchema,
});
export const notificationTriggerListResponseSchema = z.object({
    items: z.array(notificationTriggerSchema),
});
export const notificationTriggerPatchSchema = z
    .object({
    name: z.string().trim().min(1).max(NAME_MAX).optional(),
    channel: notificationChannelEnum.optional(),
    template_id: uuidSchema.nullable().optional(),
    lead_time_days: z
        .array(z.number().int().nonnegative().max(365))
        .max(20)
        .nullable()
        .optional(),
    is_enabled: z.boolean().optional(),
    quiet_hours_start: localTimeSchema.nullable().optional(),
    quiet_hours_end: localTimeSchema.nullable().optional(),
    recipient_role: z.string().min(1).max(ROLE_MAX).nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
// -------------------------------------------------------------------------
// History
// -------------------------------------------------------------------------
export const notificationHistoryItemSchema = z.object({
    id: uuidSchema,
    template_id: uuidSchema.nullable(),
    trigger_id: uuidSchema.nullable(),
    account_id: uuidSchema.nullable(),
    recipient_user_id: uuidSchema.nullable(),
    recipient_email: z.string().max(EMAIL_MAX),
    channel: notificationChannelEnum,
    subject: z.string().max(SUBJECT_MAX),
    rendered_subject: z.string().max(SUBJECT_MAX),
    message_id: z.string().nullable(),
    status: notificationStatusEnum,
    error: z.string().nullable(),
    retry_count: z.number().int().nonnegative(),
    sent_at: isoDateTimeSchema.nullable(),
    delivered_at: isoDateTimeSchema.nullable(),
    bounce_reason: z.string().nullable(),
    bounce_received_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
});
export const notificationHistoryListResponseSchema = z.object({
    items: z.array(notificationHistoryItemSchema),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative().nullable(),
});
const CURSOR_MAX = 200;
export const notificationHistoryFilterSchema = z.object({
    status: notificationStatusEnum.optional(),
    channel: notificationChannelEnum.optional(),
    account_id: uuidSchema.optional(),
    recipient_user_id: uuidSchema.optional(),
    template_id: uuidSchema.optional(),
    trigger_id: uuidSchema.optional(),
    date_from: isoDateTimeSchema.optional(),
    date_to: isoDateTimeSchema.optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
// -------------------------------------------------------------------------
// Inbox (Wave 2.32 BE-2 / TASK-217)
// -------------------------------------------------------------------------
/**
 * Severity classification for the bell-icon drawer. Matches the CHECK
 * constraint on `customers.notifications_inbox.severity`.
 */
export const notificationInboxSeverityEnum = z.enum([
    'info',
    'warning',
    'critical',
]);
/**
 * Linked entity type — drives drawer navigation. `system` is reserved for
 * notifications with no business-entity context (cron failures, etc.).
 */
export const notificationInboxEntityTypeEnum = z.enum([
    'account',
    'venue',
    'ticket',
    'system',
]);
const INBOX_TITLE_MAX = 255;
const INBOX_BODY_MAX = 10_000;
export const notificationInboxItemSchema = z.object({
    id: uuidSchema,
    title: z.string().min(1).max(INBOX_TITLE_MAX),
    body: z.string().min(1).max(INBOX_BODY_MAX),
    severity: notificationInboxSeverityEnum,
    entity_type: notificationInboxEntityTypeEnum.nullable(),
    entity_id: uuidSchema.nullable(),
    is_read: z.boolean(),
    read_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
});
export const notificationInboxResponseSchema = z.object({
    items: z.array(notificationInboxItemSchema),
    next_cursor: z.string().nullable(),
    unread_count: z.number().int().nonnegative(),
});
/**
 * `GET /admin/notifications/inbox` query params.
 *
 * `cursor` — keyset pagination payload over `(created_at, id)` DESC. The
 * backend produces opaque base64url tokens; clients echo the previous
 * page's `next_cursor`.
 *
 * `unread_only=true` narrows to `is_read = false`. `unread_count` in the
 * response is always the total unread across all pages (does NOT depend on
 * this flag).
 */
export const notificationInboxFilterSchema = z.object({
    cursor: z.string().max(CURSOR_MAX).optional(),
    limit: z.coerce.number().int().positive().max(50).default(20),
    unread_only: z.coerce.boolean().optional().default(false),
});
/**
 * `POST /admin/notifications/inbox/:id/mark-read` response — Wave 2.59 BE
 * (TASK-217-followup-mark-read).
 *
 * Endpoint is idempotent: marking an already-read row succeeds and echoes
 * the persisted `read_at` (set on the original mark, not now()). The
 * audit row `notification.marked_read` is emitted only on the transition
 * `false → true` so repeated calls do not pollute the journal.
 */
export const notificationInboxMarkReadResponseSchema = z.object({
    id: uuidSchema,
    is_read: z.literal(true),
    read_at: isoDateTimeSchema,
});
/**
 * `POST /admin/notifications/inbox/mark-all-read` response — Wave 2.59 BE
 * (TASK-217-followup-mark-read).
 *
 * `marked` is the number of rows whose state changed from unread → read
 * by this call. Returns 0 (no audit row emitted) when the inbox is
 * already fully read.
 */
export const notificationInboxMarkAllReadResponseSchema = z.object({
    marked: z.number().int().nonnegative(),
});
// -------------------------------------------------------------------------
// Quiet hours (Wave 2.35 BE-1 / TASK-099d)
// -------------------------------------------------------------------------
/**
 * Channels the quiet-hours rule may target. Only "loud" delivery channels
 * are eligible — `in_app` is silent by design and never enters this list
 * (the schema rejects it). Mirrors the CHECK constraint on
 * `internal.users.quiet_hours_channels`.
 */
export const notificationQuietHoursChannelEnum = z.enum([
    'email',
    'push',
    'telegram',
]);
/**
 * Severity classification for `customers.notification_queue.severity`.
 * `critical` bypasses the quiet-hours window unconditionally (admin-prd
 * §11.3). `low` is reserved for digest-style messages — same quiet-hours
 * treatment as `normal` for v0.1.0.
 */
export const notificationSeverityEnum = z.enum([
    'low',
    'normal',
    'critical',
]);
/**
 * `GET /admin/me/notification-settings` response + `PATCH ...` body.
 *
 * Wire contract:
 *   - `quiet_hours_start` / `quiet_hours_end` — local HH:MM in the
 *     recipient's timezone. Either both must be set or both must be null
 *     (DB-level CHECK mirrors this). A window where `start > end` is
 *     interpreted as "across midnight" (e.g. 22:00..08:00).
 *   - `quiet_hours_channels` — non-empty subset of the channel enum.
 *     Defaults to `["push"]` server-side; the FE may send `[]` to
 *     opt out of all channel-level deferral while still keeping the
 *     window for future channels.
 *   - `timezone` — IANA name. Server runtime-validates via
 *     `Intl.DateTimeFormat` (same as venue create / patch).
 */
export const notificationQuietHoursSchema = z
    .object({
    quiet_hours_start: localTimeSchema.nullable(),
    quiet_hours_end: localTimeSchema.nullable(),
    quiet_hours_channels: z
        .array(notificationQuietHoursChannelEnum)
        .max(notificationQuietHoursChannelEnum.options.length),
    timezone: ianaTimezoneSchema,
})
    .strict()
    .refine((obj) => (obj.quiet_hours_start === null && obj.quiet_hours_end === null) ||
    (obj.quiet_hours_start !== null && obj.quiet_hours_end !== null), 'quiet_hours_start and quiet_hours_end must be both set or both null');
/**
 * `PATCH /admin/me/notification-settings` body — all fields optional but at
 * least one must be supplied. Pair-wise nullability of start/end is
 * enforced only when both are present in the patch (defence-in-depth at
 * the service layer applies the combined window on persisted state).
 */
export const notificationQuietHoursPatchSchema = z
    .object({
    quiet_hours_start: localTimeSchema.nullable().optional(),
    quiet_hours_end: localTimeSchema.nullable().optional(),
    quiet_hours_channels: z
        .array(notificationQuietHoursChannelEnum)
        .max(notificationQuietHoursChannelEnum.options.length)
        .optional(),
    timezone: ianaTimezoneSchema.optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided')
    .refine((obj) => {
    const startTouched = obj.quiet_hours_start !== undefined;
    const endTouched = obj.quiet_hours_end !== undefined;
    if (!startTouched && !endTouched)
        return true;
    if (!startTouched || !endTouched)
        return false;
    const startNull = obj.quiet_hours_start === null;
    const endNull = obj.quiet_hours_end === null;
    return startNull === endNull;
}, 'quiet_hours_start and quiet_hours_end must be patched together and share nullness');
//# sourceMappingURL=notification.js.map